import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useMemo,
  useCallback,
  useState,
} from 'react';

import { useDisplay } from '../../stores';
import { getLayoutBoundingRect } from '../../utils';
import SvgColorImg from '../SvgColorImg';

const SelectDropdown = function SelectDropdown(props) {
  const {
    className,
    style,
    id,
    name,
    label,
    multiple,
    onChange,
    value,
    choices = [],
    disabled,
    enableClear,
    invalid,
  } = props;
  const { display } = useDisplay(false);
  const browserHeight = display?.browserHeight ?? globalThis.innerHeight;
  const [opened, setOpened] = useState(false);
  const [pulldownStyle, setPulldownStyle] = useState(
    {
      top: 0,
      left: 0,
      maxHeight: 0,
    },
  );
  const [pulldownLabelClassname, setPulldownLabelClassname] = useState('');

  const buttonInfo = useMemo(
    () => {
      let text;
      let icon;

      const displayValue = choices.find((thisValue) => (thisValue.value === value));

      if (!multiple) {
        if (value) {
          if (displayValue) {
            text = displayValue.text;
            icon = displayValue.icon;
          }
        }
      } else if (value?.length) {
        text = `${label} (${value.length})`;
      }

      if (!text) {
        text = label || '- - -';
      }

      const thisClassName = classNames(
        'rounded-md border p-2 flex flex-row gap-2 items-center relative justify-between',
        {
          'border-pxMainOutline': !invalid,
          'border-pxUiAlert/40': invalid,
          'text-pxPrimary': (!invalid)
                        && (!disabled)
                        && (((!multiple) && displayValue) || value?.length),
          'text-black/60': (!invalid) && (disabled || (!text)),
          'text-pxUiAlert': invalid && (((!multiple) && displayValue) || value?.length),
          'text-pxUiAlert/60': invalid && (!text),
        },
        className,
      );

      // console.log(`value=${value}, text=${text}`);
      return ({
        text,
        className: thisClassName,
        icon,
      });
    },
    [
      label, multiple, choices, value, className, invalid, disabled,
    ],
  );

  const displayChoices = useMemo(
    () => {
      if (enableClear) {
        return [
          {
            value: undefined,
            text: '-',
          },
          ...choices,
        ];
      }

      return choices;
    },
    [choices, enableClear],
  );

  const baseOnClick = useCallback(
    () => {
      if ((!disabled) && (choices.length)) {
        setOpened((prevOpened) => {
          if (!prevOpened) {
            const baseDiv = document.getElementById(id);
            // Layout px, so the values below stay valid as fixed top/left under the shell zoom
            const baseDivRect = getLayoutBoundingRect(baseDiv);

            // Note that for getBoundingClientRect, bottom
            // is measured from the top, right from the left
            // > differs from a div placement css
            const {
              left, top, bottom, width,
            } = baseDivRect;

            if (top > (browserHeight / 2)) {
              const cssBottom = browserHeight - bottom;
              const maxHeight = bottom - 10;
              setPulldownStyle({
                bottom: cssBottom,
                left,
                maxHeight,
                minWidth: width,
              });

              setPulldownLabelClassname('absolute -bottom-2 left-2 text-black/60 px-1 text-xs bg-white whitespace-nowrap');
            } else {
              const maxHeight = browserHeight - top - 10;
              setPulldownStyle({
                top,
                left,
                maxHeight,
                minWidth: width,
              });

              setPulldownLabelClassname('absolute -top-2 left-2 text-black/60 px-1 text-xs bg-white whitespace-nowrap');
            }
          }

          return (!prevOpened);
        });
      }
    },
    [disabled, browserHeight, id, choices.length],
  );

  const handleBackClick = useCallback(
    () => {
      setOpened(false);
    },
    [],
  );

  const onClick = useCallback(
    (event) => {
      const { id: buttonValue } = event.currentTarget;
      // console.log(`click on ${buttonValue} ,typeof=${typeof buttonValue}`);

      if ((!multiple) && onChange) {
        onChange({
          currentTarget: {
            id,
            name,
            value: buttonValue,
          },
        });
        setOpened(false);
      }
    },
    [id, name, multiple, onChange],
  );

  if (!opened) {
    const imgClassname = classNames(
      'w-4 h-4',
      {
        'opacity-30': disabled,
        'fill-pxUiAlert': invalid,
        'fill-black/60': (!invalid) && disabled,
        'fill-pxPrimary': (!invalid) && (!disabled),
      },
    );

    const content = (
      <>
        {buttonInfo.icon || null}
        {buttonInfo.text}
        <SvgColorImg
          src="/images/svg/gui/arrowDown.svg"
          className={imgClassname}
          alt="open"
          imgColorAsCssFill
        />

        {
          (value)
            ? (
                <div className="absolute -top-2 left-2 text-black/60 px-1 text-xs bg-white whitespace-nowrap">
                  {label}
                </div>
              )
            : null
        }
      </>
    );

    if (disabled) {
      return (
        <div
          id={id}
          className={buttonInfo.className}
          style={style}
        >
          {content}
        </div>
      );
    }

    return (
      <button
        id={id}
        type="button"
        onClick={baseOnClick}
        className={buttonInfo.className}
        style={style}
      >
        {content}
      </button>
    );
  }

  return (
    <>
      <button
        id={`${id}-holder`}
        type="button"
        onClick={baseOnClick}
        className={buttonInfo.className}
        style={style}
        disabled
      >
        {buttonInfo.text}
      </button>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,
       jsx-a11y/click-events-have-key-events */}
      <div
        className="z-50 fixed inset-0 bg-white/40"
        onClick={handleBackClick}
      />

      <div
        className="z-50 bg-white fixed rounded-md border border-pxMainOutline flex flex-col"
        style={pulldownStyle}
      >
        <div className="flex-1 flex flex-col overflow-y-auto text-black p-2 gap-2 items-start max-h-full">
          {displayChoices.map((thisChoice) => (
            <button
              id={thisChoice.value}
              type="button"
              onClick={onClick}
              key={thisChoice.value}
              className={
                classNames(
                  'w-full flex flex-row items-center justify-start gap-2 text-black whitespace-pre hover:opacity-100',
                  {
                    'font-bold': (thisChoice.value === value),
                    'opacity-50': (thisChoice.disabled),
                  },
                )
              }
              disabled={thisChoice.disabled}
            >
              {thisChoice.icon || null}
              {thisChoice.text || thisChoice.value}
            </button>
          ))}
        </div>

        <div className={pulldownLabelClassname}>
          {label}
        </div>
      </div>
    </>
  );
};

SelectDropdown.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({ }),
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  choices: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      icon: PropTypes.node,
      disabled: PropTypes.bool,
    }),
  ),
  disabled: PropTypes.bool,
  // Add a blank line at the beginning to let the user clear the selection
  enableClear: PropTypes.bool,
  invalid: PropTypes.bool,
};

export default SelectDropdown;
